/**
 * LS-693 — adapters front ↔ API du module « Documents » (consultation transverse
 * des documents d'un véhicule).
 *
 * Convention du repo :
 *  - Front : camelCase  (ex. devisReference, numeroDossier)
 *  - API   : snake_case (ex. devis_reference, numero_dossier)
 */

// ── Sources ──────────────────────────────────────────────────────

// Liste fermée côté API (enum SourceDocument) : deux valeurs seulement, et
// aucune constante ne les expose — contrairement aux types de documents.
const SOURCE_OPTIONS = [
  { value: "devis", label: "Devis" },
  { value: "financement", label: "Demande de financement" },
];

// Collection portée par la ligne de devis. C'est la seule qui ne vienne pas de
// l'enum `TypeDocument` : elle n'a pas d'enum dédié côté API, son libellé est
// écrit en dur dans `DocumentVehiculeResource::libelleType`.
const TYPE_CONDITIONS_PARTICULIERES = {
  value: "conditions_particulieres",
  label: "Conditions Particulières",
};

/**
 * Options du filtre « type de document ».
 *
 * Les types de financement viennent de `FINANCEMENT.TYPES_DOCUMENT` (constantes
 * API, enum `TypeDocument` au complet) et non de `TYPES_DOCUMENT_UPLOAD_MANUEL`,
 * qui exclut les types non déposables à la main (tableau récapitulatif, accord
 * de financement) — ceux-ci existent bel et bien et doivent rester filtrables ici.
 */
const typeOptionsFromConstants = (constantData) => [
  TYPE_CONDITIONS_PARTICULIERES,
  ...(constantData?.FINANCEMENT?.TYPES_DOCUMENT || []).map((t) => ({
    value: t.id,
    label: t.name,
  })),
];

// ── Filtres → API ────────────────────────────────────────────────

// `tools.getSearchString` ignore les valeurs nulles/vides et sérialise les
// tableaux en `immatriculations[]=…`, format attendu par ListeDocumentsRequest.
const filtresDocumentsToApi = (f = {}) => ({
  immatriculation: f.immatriculation,
  immatriculations: f.immatriculations,
  client_uuid: f.clientUuid,
  catalog_uuid: f.catalogUuid,
  source: f.source,
  type: f.type,
  devis_reference: f.devisReference,
  numero_dossier: f.numeroDossier,
  date_debut: f.dateDebut,
  date_fin: f.dateFin,
});

/**
 * Payload de `POST /documents/archive`. Une sélection explicite prime sur les
 * filtres côté API : on ne les envoie donc pas ensemble, pour que le corps de
 * la requête dise exactement ce qui est demandé.
 *
 * Sans sélection, l'archive porte sur l'intégralité du résultat filtré (pas sur
 * la page courante) : ni `page` ni `par_page` ne sont transmis.
 */
const payloadArchiveToApi = (filtres = {}, uuids = []) =>
  uuids.length > 0 ? { uuids } : filtresDocumentsToApi(filtres);

// ── API → front ──────────────────────────────────────────────────

// `source`, `type` et `porte_par` arrivent sous la forme { valeur, libelle } :
// le libellé est décidé par l'API, jamais reconstruit ici.
const documentFromApi = (d = {}) => ({
  uuid: d.uuid,
  source: d.source || null,
  type: d.type || null,
  fileName: d.file_name,
  mimeType: d.mime_type,
  size: d.size,
  // URL signée 30 min sur S3 : elle périme, la liste est donc rechargée à
  // chaque changement de filtre ou de page plutôt que mise en cache.
  url: d.url,
  // `null` tant que le devis n'a pas de contrat : le document reste listé,
  // trouvable par client ou par référence de devis.
  immatriculation: d.immatriculation,
  client: d.client || null,
  catalogue: d.catalogue || null,
  // `dossierUuid` = l'uuid du dossier parent (quotations), cible du lien de la
  // colonne Devis : la route /quotations/{uuid} ne connaît pas l'uuid de la ligne.
  devis: d.devis
    ? {
        uuid: d.devis.uuid,
        reference: d.devis.reference,
        dossierUuid: d.devis.dossier_uuid || null,
      }
    : null,
  numeroDossier: d.numero_dossier,
  // Niveau réel de rattachement (devis / demande) : explique qu'un même
  // document apparaisse sous plusieurs immatriculations d'un même lot.
  portePar: d.porte_par || null,
  createdAt: d.created_at,
});

// ── Présentation ─────────────────────────────────────────────────

const MIME_ICONS = {
  "application/pdf": "file-pdf-o",
  "image/jpeg": "file-image-o",
  "image/png": "file-image-o",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "file-excel-o",
  "application/vnd.ms-excel": "file-excel-o",
};

const iconePourMime = (mimeType) => MIME_ICONS[mimeType] || "file-o";

const formatTaille = (bytes) => {
  if (!bytes) return "";
  const units = ["o", "Ko", "Mo", "Go"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex > 0 && value < 10 ? 1 : 0)} ${units[unitIndex]}`;
};

/**
 * État du bouton d'archive. Le plafond de 100 fichiers est pré-empté ici quand
 * les deux nombres sont connus (cochés, total du résultat filtré) : inutile de
 * laisser partir une requête vouée au 422. Le plafond de volume (200 Mo) n'est
 * connu que du serveur et reste traité en erreur au retour.
 */
const etatArchive = ({
  nbSelection = 0,
  total = 0,
  maxSelection,
  isArchiving = false,
}) => {
  if (nbSelection > maxSelection) {
    return {
      desactive: true,
      avertissement: `Sélection trop grande : ${nbSelection} documents cochés, maximum ${maxSelection} par archive.`,
    };
  }
  if (nbSelection === 0 && total > maxSelection) {
    return {
      desactive: true,
      avertissement: `${total} documents dans le résultat, maximum ${maxSelection} par archive : affinez les filtres ou cochez une sélection.`,
    };
  }
  if (nbSelection === 0 && total === 0) {
    return { desactive: true, avertissement: null };
  }
  return { desactive: isArchiving, avertissement: null };
};

// L'API nomme l'archive `documents-<date du jour>.zip` et le pose en
// Content-Disposition ; on la relit plutôt que de la reconstruire côté front.
const nomFichierArchive = (headers = {}) => {
  const disposition =
    headers["content-disposition"] || headers["Content-Disposition"] || "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : "documents.zip";
};

/**
 * L'archive est demandée en `responseType: "blob"` : un 422 (sélection vide,
 * plus de 100 fichiers ou 200 Mo) arrive donc en Blob et non en JSON. Il faut
 * le relire en texte pour récupérer le message, qui invite à affiner les filtres.
 */
const messageErreurArchive = async (err) => {
  const defaut = "Le téléchargement de l'archive a échoué.";
  const data = err?.response?.data;
  if (!data) return defaut;

  try {
    const texte = typeof data.text === "function" ? await data.text() : data;
    const contenu = typeof texte === "string" ? JSON.parse(texte) : texte;
    return contenu?.message || defaut;
  } catch {
    return defaut;
  }
};

const documentsAdapters = {
  SOURCE_OPTIONS,
  typeOptionsFromConstants,
  etatArchive,
  iconePourMime,
  formatTaille,
  nomFichierArchive,
  messageErreurArchive,
  toApi: {
    filtresDocuments: filtresDocumentsToApi,
    payloadArchive: payloadArchiveToApi,
  },
  fromApi: {
    document: documentFromApi,
  },
};

export default documentsAdapters;
